import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react";
import {
  ArrowTopRightOnSquareIcon,
  ChevronDownIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  SparklesIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import clsx from "clsx";
import toast from "react-hot-toast";
import { conciergeEnabled, requestPlan } from "@/onboarding/concierge/api";
import useAuth from "@/hooks/useAuth";
import { getRecordedWatchlist } from "@/demo-guide/fixtures";
import { useDemoGuideStore } from "@/demo-guide/useDemoGuideStore";
import { useWatchlistPush } from "@/demo-guide/useWatchlistPush";
import { recordedFilterDraft } from "@/demo-guide/fixtures/fromRecording";
import FilterBuilderBrowser from "./FilterBuilderBrowser";
import { demoTemplateFor } from "@/demo-guide/templates";
import { applyPlan } from "@/onboarding/concierge/applyPlan";
import PlanPreview from "@/onboarding/concierge/PlanPreview";
import ThinkingBar from "./ThinkingBar";
import { trackOnboarding } from "@/onboarding/analytics";
import type { ProvisioningPlan } from "@/onboarding/concierge/types";
import type { FilterDraft } from "../hooks/useFilterBuilder";
import { useAccessControl } from "@/hooks/useAccessControl";

const BUTTON_PRIMARY =
  "cursor-pointer rounded border border-squawk-blue bg-squawk-blue/15 px-3 py-1.5 text-xs font-semibold " +
  "text-squawk-blue transition-colors hover:bg-squawk-blue/25 disabled:cursor-not-allowed disabled:opacity-40";

/**
 * Worked examples, doubling as the fastest way in.
 *
 * An empty field with a placeholder asks the user to guess what this
 * understands. Examples answer that at a glance and cost one click — chosen to
 * span the range: a person, a commodity, a sector, an event type.
 */
/** Illustration only — never rendered as real content. */
const SAMPLE_HEADLINES = [
  {
    time: "15:24",
    text: "Gold hits record high as real yields slip",
    important: true,
  },
  {
    time: "15:15",
    text: "Newmont lifts full-year production guidance",
    important: false,
  },
  {
    time: "15:02",
    text: "Barrick flags higher grades at Nevada complex",
    important: false,
  },
];

const EXAMPLES = [
  "Fed speakers and rate cuts",
  "OPEC and crude supply",
  "US semis earnings",
  "UK inflation and the BoE",
];

const BUTTON_QUIET =
  "cursor-pointer border-none bg-transparent px-2 py-1.5 text-xs text-gray-500 transition-colors hover:text-gray-300";

export interface WatchlistDraft {
  name?: string;
  tickers?: string[];
  keywords?: string[];
  tags?: string[];
}

interface DescribeItPanelProps {
  onOpenInBuilder: (draft: FilterDraft) => void;
  /**
   * Hands a single proposed watchlist to the ordinary builder, pre-filled.
   *
   * The concierge is a faster way to fill the same form, not a second place to
   * review and save things. When what it proposes IS one watchlist, it belongs
   * in the editor the user would have typed it into — the review panel only
   * survives for plans that genuinely make several resources at once and so
   * cannot be shown in a single form.
   */
  onOpenWatchlist?: (draft: WatchlistDraft) => void;
  onCreated?: () => void;
  /**
   * "hero" puts the input on the page as the primary way in; "button" is the
   * compact affordance for a toolbar once the user is already building.
   */
  variant?: "hero" | "button";
}

/**
 * The concierge entry, sitting in the builder's header as the alternative to
 * typing a list out by hand: say what you want to follow and it assembles the
 * watchlists and filters, checking the Newsquawk library first.
 *
 * A compact button rather than a landing page — the builder is the page's
 * job, and this is the shortcut past it. Everything proposed is reviewed and
 * editable before anything is written, which is the hallucination gate.
 */
/**
 * How to reach a list that already exists — its own block, below the verdict.
 *
 * This replaces PlanPreview for reuse rather than sitting above it. Rendering
 * both is what produced the same list name five times, its terms twice, and
 * the model's own headline on top — "ready to track" over a verdict whose
 * whole point is that nothing was created.
 *
 * The still is built from FilterPanel's actual classes: gray-900 fields,
 * gray-800 footer, the real search box, the real blue Done. A rough diagram
 * would be a picture of something they have to go and recognise; a copy is the
 * thing itself. The first word of the list is pre-typed in the search box,
 * because "search for it" is a shorter instruction than any sentence
 * describing where to look.
 */
function AccessHint({ names }: { names: string[] }) {
  const primary = names[0] ?? "";
  const firstWord = primary.split(/[\s&]+/)[0]?.toLowerCase() ?? "";
  if (!primary) return null;

  return (
    <div className="mt-5 border-t border-gray-700/50 pt-4">
      <p className="text-xs font-semibold text-gray-200">
        How to apply the existing watchlist
      </p>
      <p className="mt-1.5 text-[13px] leading-snug text-gray-300">
        Nothing to set up — it&apos;s in every widget&apos;s filter menu
        already. Search &ldquo;{firstWord}&rdquo; and tick it:
      </p>

      {/* Seated on a piece of dashboard, not on the panel.
          A widget only reads as an object because it is a lighter surface on a
          darker one: gray-900 with a gray-800 border, against the gray-950 the
          dashboard body paints (#030712, index.css). Dropped straight into this
          panel — itself gray-900 — the border separated nothing and the whole
          still went flat, which is the state this fixes. The inset restores the
          contrast the copied classes were always relying on. */}
      <div
        aria-hidden
        className="pointer-events-none mt-3 select-none rounded-md bg-gray-950 p-3 ring-1 ring-inset ring-gray-800/60"
      >
        <div className="overflow-hidden rounded-t-md border border-gray-800 bg-gray-900 shadow-lg shadow-black/20">
          {/* WidgetContainer's header: same bar, same 3.5 icons, and the funnel
            lit because a panel is open — which is the state being depicted. */}
          <div className="flex select-none items-center justify-between bg-gray-800/80 px-2 py-0.5 text-2xs text-white">
            <span>Newsquawk Headlines</span>
            <div className="flex items-center">
              <span className="p-1">
                <PencilSquareIcon className="h-3.5 w-3.5 text-white" />
              </span>
              <span className="bg-squawk-blue p-1">
                <FunnelIcon className="size-4 text-white" />
              </span>
              <span className="p-1">
                <MagnifyingGlassIcon className="h-3.5 w-3.5 text-white" />
              </span>
              <span className="p-1">
                <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 text-white" />
              </span>
              <span className="p-1">
                <XMarkIcon className="h-3.5 w-3.5 text-white" />
              </span>
            </div>
          </div>

          {/* FilterPanel's search box. Blue border because it autofocuses. */}
          <div className="bg-gray-800 px-3 pb-2 pt-2">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-gray-500" />
              <div className="w-full rounded border border-squawk-blue bg-gray-900 py-1.5 pl-8 pr-8 text-xs text-gray-200">
                {firstWord}
              </div>
              <XMarkIcon className="absolute right-2 top-1/2 size-3 -translate-y-1/2 text-gray-500" />
            </div>
          </div>

          <div className="bg-gray-800 px-3 pb-2">
            <div className="flex items-center gap-1.5 py-1">
              <ChevronDownIcon className="size-3.5 text-gray-400" />
              <span className="flex-1 text-xs font-medium text-gray-200">
                Watchlists
              </span>
            </div>
            <p className="px-1 pb-0.5 pt-1 text-2xs font-semibold uppercase tracking-wider text-gray-500">
              Newsquawk
            </p>
            {names.map((name) => (
              <div key={name} className="flex items-center gap-2 rounded px-1">
                <span className="flex w-6 shrink-0 justify-center py-1">
                  <span className="size-3.5 rounded border border-gray-600 bg-gray-900" />
                </span>
                <span className="min-w-0 flex-1 truncate py-1 text-xs text-gray-200">
                  {name}
                </span>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between gap-2 border-t border-gray-700/30 bg-gray-800 px-3 py-2">
            <span className="flex items-center gap-1.5 text-xs text-gray-500">
              <ArrowTopRightOnSquareIcon className="size-3.5" />
              Manage in Library
            </span>
            <span className="rounded bg-squawk-blue px-3 py-1 text-xs font-medium text-white">
              Done
            </span>
          </div>

          {/* NewsquawkHeadlineItem's own row: text-xs, semibold, py-1, and the
            time in a w-10 column. Orange is an important headline. */}
          <div>
            {SAMPLE_HEADLINES.map((headline) => (
              <div
                key={headline.text}
                className={clsx(
                  "relative flex w-full items-start gap-1 py-1 pr-4 text-xs font-semibold",
                  headline.important
                    ? "bg-terminal-orange text-black"
                    : "text-gray-100"
                )}
              >
                <span className="w-10 whitespace-nowrap px-1 text-left text-xs">
                  {headline.time}
                </span>
                <span className="truncate">{headline.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function DescribeItPanel({
  onOpenInBuilder,
  onOpenWatchlist,
  onCreated,
  variant = "button",
}: DescribeItPanelProps) {
  const queryClient = useQueryClient();
  const { hasAccessToWidget } = useAccessControl();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [prefilled, setPrefilled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [viewingDemoQuery, setViewingDemoQuery] = useState(false);
  /**
 * What the apply is doing right now.
 *
 * A plan with two ticker lists is several seconds of tag lookups before the
 * first write lands, and a button stuck on "Saving…" through all of it reads
 * as hung — which is exactly how it was reported.
 */
const [progress, setProgress] = useState<string | null>(null);
  const [plan, setPlan] = useState<ProvisioningPlan | null>(null);

  /**
   * Demo replay.
   *
   * /plan is guarded by require_subscriber and answers 401 to a logged-out
   * visitor, so the assisted route cannot run for the audience the guided demo
   * sends here — it would offer the feature and then fail on submit. Instead
   * the box arrives prefilled with the query that produced a recording for
   * their asset class, and submitting replays it. Labelled as a recording
   * where it is
   * shown; see demo-guide/fixtures.
   */
  const { authenticated } = useAuth();
  const demoAssetClass = useDemoGuideStore((s) => s.state?.assetClass);
  const markWatchlistCreated = useDemoGuideStore((s) => s.markWatchlistCreated);
  /** True only while the guided run is actually asking for this button. */
  const watchlistPush = useWatchlistPush();
  const recorded =
    !authenticated && demoAssetClass
      ? getRecordedWatchlist(demoAssetClass)
      : null;

  /**
   * Fallback for when no concierge run has been recorded for this asset class. The
   * demo template carries a watchlist regardless, so the builder still shows
   * something real rather than an empty box the visitor cannot submit.
   */
  const demoTemplate =
    !authenticated && demoAssetClass ? demoTemplateFor(demoAssetClass) : null;
  const demoFallback = demoTemplate?.watchlist ?? null;
  const demoActive = !!recorded || !!demoFallback;

  if (!conciergeEnabled) return null;

  /**
   * Where a plan belongs, by what it actually proposes.
   *
   * The panel used to ask one question — "is this exactly one portfolio?" —
   * and send everything else to the review panel. That mis-sorted the two
   * best answers the concierge gives. A plan that reuses the desk's lists and
   * creates NOTHING is the ideal outcome and was landing in a review panel
   * with nothing to review and no button to press; a plan that combines two
   * lists is a watchlist too, and the builder has a route for exactly that.
   */
  type Outcome =
    | { kind: "watchlist" }
    | { kind: "combination" }
    | { kind: "reuse" }
    | { kind: "review" };

  const classify = (candidate: ProvisioningPlan): Outcome => {
    const { portfolios, tags_watchlists, headline_queries } = candidate;
    const created =
      portfolios.length + tags_watchlists.length + headline_queries.length;
    if (created === 0) {
      return (candidate.reused_refs?.length ?? 0) > 0
        ? { kind: "reuse" }
        : { kind: "review" };
    }
    if (created === 1 && portfolios.length === 1) return { kind: "watchlist" };
    /**
     * A combination can carry its own supporting lists, so the test is what
     * those lists contain rather than how many there are.
     *
     * openCombineRoute calls applyPlan, which writes the portfolios before the
     * user has approved anything. Keyword lists are safe to create unasked —
     * they match text and are trivially edited. Model-generated TICKERS are
     * not: they are the thing the review panel exists to catch, so any plan
     * carrying them stays in review however few resources it creates. Tag
     * watchlists likewise, since their tag ids get no preview on this route.
     */
    if (
      headline_queries.length === 1 &&
      tags_watchlists.length === 0 &&
      portfolios.every((p) => p.tickers.length === 0)
    ) {
      return { kind: "combination" };
    }
    return { kind: "review" };
  };

  const proposesSomething = (candidate: ProvisioningPlan) =>
    candidate.portfolios.length > 0 ||
    candidate.tags_watchlists.length > 0 ||
    candidate.headline_queries.length > 0;

  const close = () => {
    setOpen(false);
    setPlan(null);
    setText("");
  };

  /**
   * In the demo the box is fixed, not just prefilled.
   *
   * There is exactly one recorded plan per asset class and no way to make another —
   * /plan is guarded by require_subscriber — so anything else typed here would
   * come back answered by a plan for a different sentence. Letting the field
   * be edited offered a promise the demo cannot keep; locking it and saying
   * why keeps the feature honest and still shows it working end to end.
   */
  const demoLocked = demoActive;
  /**
   * Said at a size it can be read at.
   *
   * This was text-2xs gray-500 — the smallest and faintest thing on the page,
   * carrying the one fact that stops a field you cannot type in reading as a
   * broken field. Blue because that is already this builder's colour for "the
   * app is telling you something", and the same blue the locked input's focus
   * ring uses.
   */
  const demoNote = demoLocked && (
    <p
      id="describe-it-demo-note"
      className="mt-2.5 text-sm font-medium leading-snug text-squawk-blue"
    >
      A saved example for your asset class. On a trial you can type anything
      here and
      it builds against the live Newsquawk library.
    </p>
  );

  // Seed the box with the query that produced the recording, so the demo
  // shows the feature working rather than an empty field they cannot use.
  if (demoActive && !prefilled && !text) {
    setPrefilled(true);
    setText(recorded?.query ?? demoTemplate!.watchlistPrompt);
  }

  const handleBuild = async (override?: string) => {
    const query = (override ?? text).trim();
    if (query.length < 3 || loading) return;
    setLoading(true);
    setPlan(null);
    trackOnboarding("concierge_requested", "builder");
    try {
      // Logged out: replay rather than request. Short pause so it does not
      // look like a lookup table, short enough not to pretend to think.
      if (demoActive) {
        await new Promise((resolve) => setTimeout(resolve, 1400));
        if (recorded) {
          setPlan(recorded.plan);
          /**
           * The plan IS the built watchlist, so the step ends here.
           *
           * Without this the run stopped at its best moment: the visitor
           * watched the product assemble a list for their asset class and the rail
           * sat on "Press Build it for me" with the button already pressed and
           * nothing left to press. The other demo path (no recording, so the
           * draft goes to the ordinary builder) reaches the same call via
           * useWatchlistCreator; the store guards on watchlistCreatedAt, so
           * the two can never double-count.
           */
          markWatchlistCreated();
        } else if (onOpenWatchlist && demoFallback) {
          // No recording: hand the asset class's watchlist to the ordinary builder
          // pre-filled, which is exactly what a single-watchlist plan does.
          onOpenWatchlist({
            name: demoFallback.name,
            tickers: demoFallback.tickers,
            keywords: demoFallback.keywords,
          });
        }
        setLoading(false);
        return;
      }

      // "onboarding" would also propose a dashboard tab; here we only want
      // the watchlists and filters the description implies.
      const response = await requestPlan(query, "watchlist");

      // Land the plan in the route that matches its shape. The review panel
      // is the fallback for genuinely multi-resource plans, not the default.
      const proposed = response.plan;
      const outcome = classify(proposed);

      // One watchlist and nothing else? That is exactly what the builder makes,
      // so open it there rather than showing a second, competing editor.
      if (outcome.kind === "watchlist" && onOpenWatchlist) {
        const [only] = proposed.portfolios;
        onOpenWatchlist({
          name: only.name,
          tickers: only.tickers,
          keywords: only.keywords,
        });
        setText("");
        return;
      }

      // A combination IS a watchlist as far as the user is concerned — the
      // builder saves it through the same form. Hand it straight to the
      // combine route rather than making them approve a preview first.
      if (outcome.kind === "combination") {
        await openCombineRoute(proposed);
        setText("");
        return;
      }

      setPlan(proposed);
    } catch (error) {
      console.error("[concierge] describe-it failed:", error);
      /**
       * 503 and 429 both mean "not now" rather than "that request was bad",
       * and for both the service writes copy meant for the user into `detail`
       * — including, for a rate limit, how long the wait actually is. Other
       * statuses keep the generic line: a 422's `detail` is a validation
       * structure and a 500's is whatever leaked, neither fit to show.
       */
      const status = (error as { response?: { status?: number } })?.response
        ?.status;
      const detail = (error as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail;
      const retryable = status === 503 || status === 429;
      toast.error(
        retryable
          ? (detail ??
              (status === 429
                ? "You've used your setup requests for now — try again later."
                : "Claude is busy right now — try again in a few seconds."))
          : "Couldn't build that right now — try again, or add terms by hand."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!plan || creating) return;
    setCreating(true);
    try {
      const result = await applyPlan(plan, {
        hasAccessToWidget,
        onProgress: setProgress,
        // Tags land after the save returns; refetch so they appear without a reload.
        onTagsBackfilled: () =>
          queryClient.invalidateQueries({ queryKey: ["get-portfolios"] }),
      });
      const total =
        result.createdPortfolios +
        result.createdTagWatchlists +
        result.createdQueries;
      queryClient.invalidateQueries({ queryKey: ["get-portfolios"] });
      queryClient.invalidateQueries({ queryKey: ["tagsWatchlists"] });
      queryClient.invalidateQueries({ queryKey: ["headlineQueries"] });
      trackOnboarding("concierge_applied", "builder");
      // The builder now returns to your lists with these at the top, so the
      // toast no longer has to say where to look.
      toast.success(
        total === 1 ? "Watchlist created" : `Created ${total} watchlists`
      );
      close();
      onCreated?.();
    } catch (error) {
      console.error("[concierge] create failed:", error);
      toast.error("Couldn't save that — try again, or add terms by hand.");
    } finally {
      setCreating(false);
      setProgress(null);
    }
  };

  /** Creates the filter's watchlists, then hands it to the AND/OR builder. */
  const openCombineRoute = async (source: ProvisioningPlan) => {
    const query = source.headline_queries[0];
    if (!query || creating) return;
    setCreating(true);
    try {
      const { refToId } = await applyPlan(
        { ...source, headline_queries: [], dashboard_tab: null },
        {
          hasAccessToWidget,
          onProgress: setProgress,
          onTagsBackfilled: () =>
            queryClient.invalidateQueries({ queryKey: ["get-portfolios"] }),
        }
      );
      const resolve = (refs: string[]) =>
        refs
          .map((ref) => refToId[ref])
          .filter((id): id is number => typeof id === "number");
      queryClient.invalidateQueries({ queryKey: ["get-portfolios"] });
      queryClient.invalidateQueries({ queryKey: ["tagsWatchlists"] });
      onOpenInBuilder({
        name: query.name,
        searchKeyword: query.search_keyword || undefined,
        tags: query.tags,
        portfolioIds: resolve(query.portfolio_refs),
        tagsWatchlistIds: resolve(query.tags_watchlist_refs),
        filterMode: query.filter_op,
        keywordOp: query.keyword_op,
      });
      close();
    } catch (error) {
      console.error("[concierge] open-in-builder failed:", error);
      toast.error("Couldn't prepare that — try again, or add terms by hand.");
    } finally {
      setCreating(false);
      setProgress(null);
    }
  };

  const handleOpenInBuilder = () => plan && openCombineRoute(plan);

  /**
   * "These lists already cover it" — hand them to the combine route selected.
   *
   * The lists themselves already exist and need no creating; what the user
   * lacks is one saved thing that points at them. Resolving the lib: names to
   * ids and opening the combine route pre-selected is that, and it is the
   * same route they would have reached by hand.
   */
  const handleUseExisting = async () => {
    if (!plan || creating) return;
    setCreating(true);
    try {
      // Creates nothing: a reuse-only plan has no portfolios, tag watchlists
      // or queries to write. This resolves lib:/libq:<name> -> real id, and
      // hands back the library filters behind any libq: refs.
      const { refToId, libraryQueries } = await applyPlan(plan, {
        hasAccessToWidget,
        onProgress: setProgress,
        // Tags land after the save returns; refetch so they appear without a reload.
        onTagsBackfilled: () =>
          queryClient.invalidateQueries({ queryKey: ["get-portfolios"] }),
      });
      const portfolioIds = (plan.reused_refs ?? [])
        .filter((ref) => ref.startsWith("lib:"))
        .map((ref) => refToId[ref])
        .filter((id): id is number => typeof id === "number");
      // Their own words are the name — see below.
      const name = text.trim().slice(0, 100);

      /**
       * A library filter: open an editable copy of it.
       *
       * libq: refs were dropped here before, so any answer that was a filter
       * rather than a watchlist ("Trade & Tariffs" for "Trump") hit the error
       * toast even though applyPlan had found it. A filter is a recipe, so the
       * copy carries all of it — tags, keyword, lists and every AND/OR — and
       * saving gives them their own filter to edit.
       *
       * Only the first filter is copied; two recipes with their own AND/OR do
       * not merge into one. Library watchlists from the same answer are added
       * only when the filter unions its lists (OR), where one more list widens
       * it the way the answer meant. Under AND it would narrow it instead.
       */
      const filter = (plan.reused_refs ?? [])
        .filter((ref) => ref.startsWith("libq:"))
        .map((ref) => libraryQueries[ref])
        .find(Boolean);
      if (filter) {
        const filterMode = filter.filterMode ?? "OR";
        const extraIds = filterMode === "OR" ? portfolioIds : [];
        onOpenInBuilder({
          name,
          searchKeyword: filter.searchKeyword || undefined,
          tags: filter.categories,
          portfolioIds: [
            ...new Set([...(filter.portfolioIds ?? []), ...extraIds]),
          ],
          tagsWatchlistIds: filter.tagsWatchlistIds ?? [],
          filterMode,
          keywordOp: filter.keywordOp,
          keywordWatchlistOp: filter.keywordWatchlistOp,
        });
        close();
        return;
      }

      if (portfolioIds.length === 0) {
        // Not a permissions problem, though the old wording ("not available
        // on this account") read as one: every account sees the library.
        // This is a name that no longer matches anything in it.
        toast.error(
          "Couldn't find that in the Newsquawk library — try describing it again, or add terms by hand."
        );
        return;
      }
      /**
       * Named from what they asked for.
       *
       * The modal opened with an empty name field and a desk list already in
       * the query, which reads as a form someone else half-filled. Their own
       * words are the best name available and they can overwrite it.
       */
      onOpenInBuilder({
        name,
        portfolioIds,
        filterMode: "OR",
      });
      close();
    } catch (error) {
      console.error("[concierge] use-existing failed:", error);
      toast.error("Couldn't open those — try again, or add terms by hand.");
    } finally {
      setCreating(false);
      setProgress(null);
    }
  };

  const outcome = plan ? classify(plan) : null;
  const isReuse = outcome?.kind === "reuse";

  /**
   * The recorded plan's combined watchlist, as the read-only viewer wants it.
   *
   * Derived from the recording rather than from `plan`, because `plan` is
   * editable state and this is meant to show what was built, not what the
   * panel currently holds. Null when the recording proposes no combination.
   *
   * A plain const rather than useMemo: this sits below the `conciergeEnabled`
   * early return, so a hook here would be a conditional one. The work is
   * building two small maps, and only when a recording is on screen.
   */
  const demoBuild =
    demoLocked && recorded ? recordedFilterDraft(recorded) : null;

  // "lib:<name>" / "libq:<name>" are the plan's own references to a desk list
  // or desk filter, and the name is the stable key across environments — the
  // same thing the filter menu shows. Filters count: combined watchlists sit in
  // the same Watchlists → Newsquawk list in that menu, so the hint applies.
  const reusedNames = (plan?.reused_refs ?? []).flatMap((ref) =>
    ref.startsWith("libq:")
      ? [ref.slice(5)]
      : ref.startsWith("lib:")
        ? [ref.slice(4)]
        : []
  );

  // The live records behind those names, for their terms. Name is the stable
  // key across environments — the same thing `lib:` refs resolve on.

  /**
   * Reuse gets its own verdict line. "This needs more than one watchlist"
   * over a plan that needs NO watchlist read as an apology for a good answer.
   */
  /**
   * A section label, not a second verdict. It read "Already covered — you can
   * use this now" directly above a sentence saying the list covers this, which
   * was two of the five places the same fact appeared.
   */
  const panelTitle = isReuse
    ? "Already covered"
    : "This needs more than one watchlist — check it over";

  /**
   * Shared by both render paths.
   *
   * This panel renders twice — inline as the "hero" on the builder page, and
   * as a dialog from a widget toolbar — and the guidance was added to the
   * dialog only, so the page most people meet it on never showed it. Anything
   * that belongs to the verdict rather than the layout has to live out here.
   */
  /**
   * Shared by both render paths — inline "hero" on the Watchlists page, and
   * the dialog from a widget toolbar. Anything belonging to the verdict rather
   * than the layout has to live out here or it drifts the moment one is edited.
   */
  /**
   * Shown after the plan card, not inside it.
   *
   * It sat between the list's terms and the buttons, which is exactly where
   * the reader is working out what the list holds and whether they want more
   * — an aside about where to find it later, wedged into the middle of a
   * decision. As its own block below, it answers a different question in a
   * place where that question is the only one being asked.
   */
  const reuseGuidance = isReuse && <AccessHint names={reusedNames} />;

  /**
   * Rendered by both paths — the inline hero on the Watchlists page and the
   * dialog from a widget toolbar — for the same reason `reuseGuidance` is:
   * anything belonging to the verdict rather than the layout drifts the moment
   * it is added to only one of them.
   */
  const demoQueryModal = demoBuild && (
    <FilterBuilderBrowser
      isOpen={viewingDemoQuery}
      onClose={() => setViewingDemoQuery(false)}
      draft={demoBuild.draft}
      portfoliosOverride={demoBuild.portfolios}
    />
  );

  const planActions = plan && (
    <>
      {/* Named steps rather than a spinner: the slow part is matching every
          term to a tag, which is worth saying — it is why a two-list plan
          takes a few seconds, and silence there read as a hang. */}
      {creating && progress && (
        <span className="mr-auto truncate text-xs text-gray-400">
          {progress}
        </span>
      )}
      {/**
       * The demo stops at the plan.
       *
       * Save and Build-on-it both call applyPlan, which writes through
       * pref-storage with the visitor's token — and a logged-out visitor has
       * none, so both answered 401 and put an error toast at the end of the
       * one flow meant to sell the feature. The plan itself is the whole
       * point here; what follows it needs an account, so this says that
       * instead of offering a button that cannot work.
       *
       * A line, not a second CTA: FlowCompleteCta is deliberately the only
       * hard ask in the run, and widget-level asks were removed for making
       * the product read as restricted rather than premium.
       */}
      {demoLocked && (
        <span className="mr-auto text-xs text-gray-400">
          Saving needs an account — on a trial this builds straight into your
          library.
        </span>
      )}
      <button
        type="button"
        onClick={() => setPlan(null)}
        disabled={creating}
        className={BUTTON_QUIET}
      >
        {isReuse || demoLocked ? "Close" : "Discard"}
      </button>
      {/**
       * The demo's one action: look at what was just built.
       *
       * Signed in, this plan offers "Open in builder" for exactly this — and
       * without an equivalent the demo asserts a watchlist was assembled and
       * never shows it, which is the one claim the run most needs to land.
       *
       * Not the signed-in route though: openCombineRoute calls applyPlan,
       * which writes through pref-storage and 401s for this audience. The
       * read-only viewer takes whole objects rather than ids, so the same
       * contents can be shown with nothing written — see fromRecording.
       */}
      {demoLocked && demoBuild && (
        <button
          type="button"
          onClick={() => setViewingDemoQuery(true)}
          className={BUTTON_PRIMARY}
          data-analytics-id="demo-guide-view-watchlist"
        >
          View watchlist
        </button>
      )}
      {demoLocked ? null : isReuse ? (
        <button
          type="button"
          onClick={handleUseExisting}
          disabled={creating}
          className={BUTTON_PRIMARY}
        >
          {creating ? "Opening…" : "Build on it"}
        </button>
      ) : (
        <>
          {plan.headline_queries.length > 0 && (
            <button
              type="button"
              onClick={handleOpenInBuilder}
              disabled={creating}
              className="cursor-pointer rounded border border-gray-700 bg-gray-900 px-3 py-1.5 text-xs font-medium text-gray-300 transition-colors hover:border-gray-600 hover:text-gray-100"
            >
              Open in builder
            </button>
          )}
          {proposesSomething(plan) && (
            <button
              type="button"
              onClick={handleCreate}
              disabled={creating}
              className={BUTTON_PRIMARY}
            >
              {creating ? "Saving…" : "Save"}
            </button>
          )}
        </>
      )}
    </>
  );

  if (variant === "hero") {
    return (
      <div>
        <div className="flex gap-2">
          <input
            autoFocus
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleBuild();
            }}
            placeholder="Describe it in your own words…"
            disabled={loading}
            readOnly={demoLocked}
            aria-describedby={demoLocked ? "describe-it-demo-note" : undefined}
            maxLength={500}
            className={clsx(
              "min-w-0 flex-1 rounded border border-gray-600 bg-gray-900 px-4 py-3.5 text-base text-gray-100 placeholder:text-gray-500 focus:outline-none disabled:opacity-60",
              demoLocked
                ? "cursor-default focus:border-gray-600"
                : "focus:border-squawk-blue focus:ring-1 focus:ring-squawk-blue/40"
            )}
          />
          <button
            type="button"
            onClick={() => handleBuild()}
            disabled={loading || text.trim().length < 3}
            className={clsx(
              "shrink-0 cursor-pointer rounded border px-6 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-40",
              // The one thing the guide is asking for, marked the way the
              // sidebar item that sent them here was marked. Stops once it has
              // been pressed — a control still pulsing after it has done its
              // job is asking for a second press that does nothing.
              watchlistPush && !loading && !plan
                ? "nq-demo-pulse border-squawk-blue"
                : "border-squawk-blue bg-squawk-blue/15 text-squawk-blue hover:bg-squawk-blue/25"
            )}
          >
            {loading ? "Checking the library…" : "Build it"}
          </button>
        </div>

        <ThinkingBar active={loading} />

        {demoNote}

        {/* Hidden in the demo: every example replays the one recording for
            this asset class, so offering four would answer "OPEC and crude supply"
            with a plan about megacap tech. */}
        <div
          className={clsx(
            "mt-2.5 flex-wrap items-center gap-2",
            demoLocked ? "hidden" : "flex"
          )}
        >
          <span className="text-xs font-medium text-gray-400">Try</span>
          {EXAMPLES.map((example) => (
            <button
              key={example}
              type="button"
              disabled={loading}
              onClick={() => {
                setText(example);
                handleBuild(example);
              }}
              className="cursor-pointer rounded-full border border-gray-600 bg-gray-800 px-3 py-1 text-xs text-gray-300 transition-colors hover:border-squawk-blue hover:text-squawk-blue disabled:opacity-40"
            >
              {example}
            </button>
          ))}
        </div>

        {plan && !loading && (
          <div className="mt-3 rounded border border-gray-700/50 bg-gray-900 text-left">
            <div className="border-b border-gray-700/50 bg-gray-800/60 px-3 py-1.5">
              <span className="text-xs font-semibold text-gray-200">
                {panelTitle}
              </span>
            </div>
            <div className="max-h-[40vh] overflow-y-auto p-3">
              <PlanPreview plan={plan} onChange={setPlan} />
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2 border-t border-gray-700/50 px-3 py-2">
              {planActions}
            </div>
          </div>
        )}
        {plan && !loading && reuseGuidance}
        {demoQueryModal}
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="Say what you want to follow and we'll assemble it"
        className="flex shrink-0 cursor-pointer items-center gap-1.5 rounded border border-squawk-blue/50 bg-squawk-blue/10 px-2.5 py-1 text-xs font-semibold text-squawk-blue transition-colors hover:bg-squawk-blue/20"
      >
        <SparklesIcon className="size-3.5" />
        Describe it instead
      </button>

      <Dialog open={open} onClose={close} className="no-drag relative z-50">
        <DialogBackdrop className="fixed inset-0 bg-gray-900/90" />
        <div className="fixed inset-0 z-10 flex items-start justify-center p-4 pt-[10vh]">
          <DialogPanel className="w-full max-w-2xl overflow-hidden rounded-lg border border-gray-700/50 bg-gray-800 shadow-xl">
            <div className="flex items-center gap-3 border-b border-gray-700/50 px-4 py-2.5">
              <SparklesIcon className="size-4 text-squawk-blue" />
              <h2 className="flex-1 text-sm font-semibold text-gray-100">
                Describe what you want to follow
              </h2>
              <button
                onClick={close}
                aria-label="Close"
                className="cursor-pointer border-none bg-transparent p-1 text-gray-400 transition-colors hover:text-gray-200"
              >
                <XMarkIcon className="size-5" />
              </button>
            </div>

            <div className="p-4">
              <p className="mb-2 text-2xs text-gray-500">
                {demoLocked ? (
                  <>
                    This is what the assisted builder was asked for your asset
                    class. We check the Newsquawk library first and only build
                    what
                    isn&apos;t already covered.
                  </>
                ) : (
                  <>
                    In your own words — &ldquo;Fed speakers and anything on rate
                    cuts&rdquo;. We check the Newsquawk library first and only
                    build what isn&apos;t already covered.
                  </>
                )}
              </p>
              <div className="flex gap-2">
                <input
                  autoFocus
                  type="text"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleBuild();
                  }}
                  placeholder="e.g. OPEC, crude supply and the energy majors"
                  readOnly={demoLocked}
                  aria-describedby={demoLocked ? "describe-it-demo-note" : undefined}
                  maxLength={500}
                  className={clsx(
                    "min-w-0 flex-1 rounded border border-gray-700 bg-gray-900 px-2 py-1.5 text-xs text-gray-200 placeholder:text-gray-500 focus:outline-none",
                    demoLocked ? "cursor-default" : "focus:border-squawk-blue"
                  )}
                />
                <button
                  type="button"
                  onClick={() => handleBuild()}
                  disabled={loading || text.trim().length < 3}
                  className={BUTTON_PRIMARY}
                >
                  {loading ? "Checking the library…" : "Build"}
                </button>
              </div>

              {demoNote}

              <ThinkingBar active={loading} />

              {plan && !loading && (
                <div className="mt-3 rounded border border-gray-700/50 bg-gray-900">
                  <div className="border-b border-gray-700/50 bg-gray-800/60 px-3 py-1.5">
                    <span className="text-2xs font-semibold uppercase tracking-wider text-gray-400">
                      {isReuse
                        ? "Already covered"
                        : "Proposed — review before saving"}
                    </span>
                  </div>
                  <div className="max-h-[45vh] overflow-y-auto p-3">
                    <PlanPreview plan={plan} onChange={setPlan} />
                  </div>
                  <div className="flex flex-wrap items-center justify-end gap-2 border-t border-gray-700/50 px-3 py-2">
                    {planActions}
                  </div>
                </div>
              )}
              {plan && !loading && reuseGuidance}
            </div>
          </DialogPanel>
        </div>
      </Dialog>
      {demoQueryModal}
    </>
  );
}
